<template>
  <tile>
    <Loader v-if="loading"></Loader>
    <div v-show="!loading">
      <div class="header">
        <p class="title">
          Student Information&nbsp;<a
            :href="undefined"
            class="edit-link"
            v-if="!loading && editable"
            @click="handleEditClick"
            >Edit</a
          >
        </p>
      </div>
      <Icon
        class="toggle"
        :icon="showDetails ? 'chevron-up' : 'chevron-down'"
        @click="showDetails = !showDetails"
      />
      <div class="applicant-info" id="student">
        <div class="section" id="student-information" :class="loading ? 'hide' : ''">
          <div class="info-section">
            <p class="info-title">Name</p>
            <div class="info">
              <p>
                {{ student?.fullName }}
              </p>
            </div>
          </div>
          <div class="info-section">
            <p class="info-title">DOB</p>
            <div class="info">
              <p>
                {{ student?.dateOfBirthDisplay }}
              </p>
            </div>
          </div>
        </div>
        <CollapsibleSection
          :inverted="showDetails === true"
          :isVisible="showDetails"
          @toggle="showDetails = !showDetails"
        >
          <div class="section" id="student-information-collapsible" v-show="showDetails">
            <div class="info-sections"></div>
            <div class="info-sections"></div>

            <div
              class="info-section"
              v-for="(detail, index) in details"
              :key="index"
              :class="detail.width"
            >
              <p class="info-title">{{ detail?.label }}</p>
              <div class="info">
                <p>
                  {{ detail?.value }}
                </p>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  </tile>

  <ModalFade :isVisible="showStudentInformationModal">
    <StudentInformationModal
      v-if="showStudentInformationModal"
      :loading="loading"
      :disabled="disabled"
      :student="student"
      @studentUpdated="onStudentUpdated"
      @close="handleClose"
    />
  </ModalFade>
</template>
<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { useStore } from "vuex";
import Tile from "@/components/shared/Tile.vue";
import Loader from "@/components/shared/Loader.vue";
import { FormModes } from "@/models/form";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";
import { StudentVM, StudentDto } from "@/models/student";
import Icon from "@/components/shared/Icon.vue";
import StudentInformationModal from "@/components/application-info/parent-loc/student/StudentInformationModal.vue";
import {
  NationalIdNumberTypeDefinitions,
  NationalIdNumberTypeIds,
} from "@/models/nationalIdNumberTypes";
import CollapsibleSection from "@/components/transitions/CollapsibleSection.vue";
import ModalFade from "@/components/transitions/ModalFade.vue";

const store = useStore();

const props = defineProps<{
  loading: boolean;
  disabled: boolean;
  student: StudentVM | null;
}>();

const emit = defineEmits<{
  (e: "studentUpdated", student: StudentDto): void;
}>();

const {
  editable,
  handleStudentInformation,
  openStudentInformationModal,
  closeStudentInformationModal,
} = useApplicationInfo();

const mode = ref<keyof typeof FormModes>(FormModes.view);

const showDetails = ref<boolean>(false);

const showStudentInformationModal = computed<boolean>(
  () => store.state.applicationInfoModule.studentInformationModalActive,
);

const details = computed(() => {
  return [
    {
      key: "addressDisplay",
      label: "Address",
      value: props.student?.addressDisplay,
      width: "col-12",
    },
    {
      key: "nationalIdNumber",
      label:
        NationalIdNumberTypeDefinitions[
          props.student?.nationalIdNumberTypeId || NationalIdNumberTypeIds.SSN
        ]?.abbreviation,
      value: props.student?.nationalIdNumber,
      width: "col-sm-12 col-md-12 col-lg-6 col-6",
    },
    {
      key: "citizenshipStatusTypeName",
      label: "Citizenship",
      value: props.student?.citizenshipStatusName,
      width: "col-6",
    },
    {
      key: "citizenshipStatusTypeName",
      label: "Parent/Guardian Attestation",
      value: props.student?.parentAttestation?.consentGiven ? "Attested" : "N/A",
      width: "col-12",
    },
  ];
});

function changeMode(newMode: keyof typeof FormModes) {
  mode.value = newMode;
}
function handleClose() {
  changeMode(FormModes.view);
  closeStudentInformationModal();
}
function handleEditClick() {
  changeMode(FormModes.edit);
  openStudentInformationModal();
}

async function onStudentUpdated(student: StudentVM) {
  await handleStudentInformation(student);
  changeMode(FormModes.view);
  emit("studentUpdated", student);
}

watch(
  () => mode.value,
  (newVal, oldVal) => {
    if (newVal === FormModes.edit) {
      openStudentInformationModal();
    }
  },
);
</script>

<style scoped src="@/styles/application-info/info-tile.css"></style>

<style scoped>
.header {
  position: relative;
}

.toggle {
  position: absolute;
  right: 8px;
  font-size: 10px;
  color: #000;
  cursor: pointer;
}

/* Override the bootstrap padding */
.cuscd-full-wrap .col-12,
.cuscd-full-wrap .col-6 {
  padding: 0;
}
/* .section#student-information {
  display: flex;
  flex-direction: row;
}

.section#student-information > div {
	width: 50%;
} */
</style>
